/**
 * MONSTER TOWN — THE COLOUR GRADE. TIME-OF-DAY CREW (src/time/**).
 *
 * WHY DIMMING THE LIGHTS IS NOT ENOUGH, and this exists.
 *
 * First attempt at night was purely a lighting change: cool the hemisphere, drop
 * the sun, scale everything down. Measured, it worked — mean frame luminance
 * fell from 164 to 94. Looked at (shots/time/, first pass), it did not: a toon
 * material multiplies its own albedo, so a dimmed lawn is a DARKER GREEN, and a
 * town full of darker greens and darker reds reads as an overcast afternoon, not
 * as night. What actually says "night" to an eye is not less light, it is light
 * of a DIFFERENT COLOUR.
 *
 * ROUND 2 — WHY A NORMAL-BLEND PANE WAS THE WRONG COLOUR CHANGE.
 * That first fix was one full-screen pane of deep blue-plum at 30% over the
 * picture. Critic round 1, measured on the canvas: plaza #dfcb98 -> #5e5d5c,
 * road #c1a773 -> #53504b. A normal blend LERPS every pixel toward one foreign
 * colour, which is a desaturate-and-cool pass — it pulls the warm hue out
 * rather than shifting it, so the committed cosy palette left the building and
 * came back as neutral concrete. The brief says a night palette shift "must
 * stay in the committed palette family".
 *
 * A MULTIPLY CANNOT DO THAT. `dst = dst * src` SCALES the three channels of
 * whatever is already there; it can never introduce a colour that was not in
 * the picture, and the ratio that makes cream cream survives it. So the grade
 * is now:
 *
 *   1. a MULTIPLY pane   — the hue shift and the darkening (cool at night,
 *                          gold in the evening, peach at sunrise)
 *   2. an ADDITIVE pane  — a small warm lift, because a pure multiply goes
 *                          muddy in the shadow side and this is for a
 *                          three-year-old
 *
 * Both are driven from the `look.ts` table, both are HIDDEN at zero strength,
 * and both are zero from 09:00 to 15:00 — so broad daylight is bit-identical to
 * the shipped game and costs nothing.
 *
 * THE MULTIPLY PANE IS PREMULTIPLIED, AND IT HAS TO BE. three.js only wires a
 * multiply blend func for a premultiplied material; without it the pane is
 * silently drawn with whatever blend state the last transparent draw left
 * behind, which at opacity 1 is an OPAQUE SHEET OF THE TINT COLOUR over the
 * whole town. See the long note at `makePane` — that is what shipped, and it
 * made night brighter than noon.
 *
 * The fade-in is still done by walking the COLOUR toward white on the CPU
 * (`gradeStrength` below) rather than by `opacity`. Premultiplied multiply is
 * `dst * mix(1, colour, alpha)`, so either would work; the CPU blend keeps the
 * two panes reading the same way and keeps `opacity` meaning one thing in this
 * file — how much of the ADDITIVE lift is showing.
 *
 * WHY QUADS ON THE CAMERA AND NOT DIVS OVER THE CANVAS
 * A DOM overlay would be free, and wrong twice: it would sit over the HUD's
 * cream panels (which are lit by a room's lamp, not by the sky — see
 * `hudNight.ts`, which handles the HUD on purpose and separately), and photo
 * mode reads the CANVAS, so a kid's night photograph would come out in
 * daylight. A quad parented to the camera is in the picture, which is where it
 * belongs. Same trick `src/interior/ambience.ts` already uses for its vignette.
 *
 * COST: at most TWO draw calls, and only when the day is not broad daylight.
 *
 * RENDER ORDER, and the one thing that must stay true: the lamp pools in
 * `lamps.ts` draw at 9995, BETWEEN the multiply (9990) and the additive (9998).
 * A pool of lamplight is a light, so the night must not multiply it down —
 * that is what turned every street lamp into a dull decal in round 1.
 */

import * as THREE from 'three'
import { blendHex, type DayLook } from './look'

/** Neutral. The only literal in this file, and it is not a colour choice. */
const NEUTRAL = 0xffffff

export const GRADE_ORDER_MULTIPLY = 9990
export const GRADE_ORDER_ADDITIVE = 9998

interface Pane {
  readonly mesh: THREE.Mesh
  readonly material: THREE.MeshBasicMaterial
}

let mul: Pane | null = null
let add: Pane | null = null
let strength = 0

function makePane(
  camera: THREE.Camera,
  name: string,
  blending: THREE.Blending,
  order: number,
  premultipliedAlpha: boolean,
): Pane {
  const material = new THREE.MeshBasicMaterial({
    color: NEUTRAL,
    transparent: true,
    opacity: 1,
    depthTest: false,
    depthWrite: false,
    fog: false,
    toneMapped: false,
    blending,
    /**
     * ============ THE ONE LINE THAT MAKES A MULTIPLY A MULTIPLY ============
     *
     * three.js only wires the multiply blend func for a PREMULTIPLIED material.
     * `WebGLState.setBlending`, r180: the non-premultiplied branch has no case
     * for `MultiplyBlending` at all — it logs
     * `MultiplyBlending requires material.premultipliedAlpha = true` and then
     * RETURNS WITHOUT SETTING A BLEND FUNC, so the pane inherits whatever the
     * last transparent draw used (src-alpha / one-minus-src-alpha) and, at
     * opacity 1, paints the tint colour over the entire glass as a flat opaque
     * slab.
     *
     * That is exactly what shipped: at every hour outside 09:00-15:00 the whole
     * town was a sheet of lilac with the lamp pools floating on it as white
     * blobs (`npm run time-check`: night mean luminance 200 against midday's
     * 164 — the night was BRIGHTER than noon, and three of that gate's checks
     * were failing on it, console error included).
     *
     * With it true: `blendFuncSeparate(DST_COLOR, ONE_MINUS_SRC_ALPHA, ...)` and
     * the shader's own `gl_FragColor.rgb *= a`, i.e. `dst * mix(1, colour, a)` —
     * a real multiply, which is the whole reason this pane exists.
     */
    premultipliedAlpha,
  })
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), material)
  mesh.name = name
  mesh.frustumCulled = false
  mesh.renderOrder = order
  mesh.visible = false
  mesh.position.set(0, 0, -1)
  camera.add(mesh)
  return { mesh, material }
}

export function installTint(scene: THREE.Scene, camera: THREE.Camera): void {
  if (mul !== null) return
  mul = makePane(camera, 'time:gradeMultiply', THREE.MultiplyBlending, GRADE_ORDER_MULTIPLY, true)
  // Additive is correct UNPREMULTIPLIED (`SRC_ALPHA, ONE`), which is what lets
  // `opacity` fade the warm lift in and out.
  add = makePane(camera, 'time:gradeGlow', THREE.AdditiveBlending, GRADE_ORDER_ADDITIVE, false)
  // The camera has to be IN the scene graph for its children to be drawn. The
  // interiors crew already adds it; `add` on the same parent is a no-op, so
  // doing it again costs nothing and removes an install-order dependency.
  scene.add(camera)
}

function size(pane: Pane, camera: THREE.OrthographicCamera): void {
  // An orthographic camera's own half-extents ARE the visible rectangle, so the
  // pane is always exactly the glass, at any zoom and in portrait or landscape.
  pane.mesh.scale.set(Math.abs(camera.right) * 2.1, Math.abs(camera.top) * 2.1, 1)
}

/**
 * Size the panes to the view and set the grade. Called every frame; every write
 * is trivial, and the whole thing is skipped in broad daylight.
 */
export function applyTint(camera: THREE.OrthographicCamera, look: DayLook): void {
  if (mul === null || add === null) return
  const s = look.gradeStrength < 0 ? 0 : look.gradeStrength > 1 ? 1 : look.gradeStrength
  strength = s

  const mulOn = s > 0.004
  if (mul.mesh.visible !== mulOn) mul.mesh.visible = mulOn
  if (mulOn) {
    mul.material.color.setHex(blendHex(NEUTRAL, look.gradeTint, s))
    size(mul, camera)
  }

  const g = look.gradeGlowAmount < 0 ? 0 : look.gradeGlowAmount > 1 ? 1 : look.gradeGlowAmount
  const addOn = g > 0.004
  if (add.mesh.visible !== addOn) add.mesh.visible = addOn
  if (addOn) {
    add.material.color.setHex(look.gradeGlow)
    add.material.opacity = g
    size(add, camera)
  }
}

/**
 * Diagnostics: how strong the grade is right now, 0..1. Zero in broad daylight
 * (neither pane is drawn), and it is the number `time-check` reads as "the
 * moonlight wash is on the glass".
 */
export function tintStrength(): number {
  return mul === null || !mul.mesh.visible ? 0 : strength
}
